/**
 * AdminWhatsAppLogs — Admin-only view of all Interakt/WhatsApp sends across the platform.
 *
 * Additive-only: does not touch other admin pages, notifications, or auth.
 * Shows: config banner, filter bar, log table with retry, templates management,
 *        manual "Run 48h reminders" button.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Sidebar } from '../../components/Sidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Loader2, MessageSquare, RefreshCw, CheckCircle2, XCircle, AlertTriangle,
  Filter, Play, ToggleLeft, ToggleRight, Copy, Bell,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColor = (s) => {
  if (s === 'sent' || s === 'delivered') return '#00C972';
  if (s === 'failed') return '#FF3B30';
  if (s === 'queued' || s === 'read') return '#06B6D4';
  return '#FFB800'; // skipped_*
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export const AdminWhatsAppLogs = () => {
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningReminders, setRunningReminders] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  // Filters
  const [filterCampaign, setFilterCampaign] = useState('');
  const [filterTemplate, setFilterTemplate] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filterCampaign) q.append('campaign_id', filterCampaign);
      if (filterTemplate) q.append('template_name', filterTemplate);
      if (filterStatus) q.append('status', filterStatus);
      if (filterFrom) q.append('date_from', filterFrom);
      if (filterTo) q.append('date_to', filterTo);

      const [logsRes, tplRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/whatsapp/logs?${q.toString()}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/whatsapp/templates`, { withCredentials: true }),
      ]);
      setConfig(logsRes.data.config);
      setLogs(logsRes.data.logs || []);
      setTemplates(tplRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const retry = async (id) => {
    try {
      await axios.post(`${API_URL}/api/admin/whatsapp/logs/${id}/retry`, {}, { withCredentials: true });
      toast.success('Retry queued');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    }
  };

  const toggleTemplate = async (t) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/whatsapp/templates/${t.id}`,
        { is_active: !t.is_active },
        { withCredentials: true },
      );
      toast.success(!t.is_active ? 'Template activated' : 'Template deactivated');
      load();
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const updateTemplateCodeName = async (t, newCodeName) => {
    if (!newCodeName || newCodeName === t.code_name) return;
    try {
      await axios.patch(
        `${API_URL}/api/admin/whatsapp/templates/${t.id}`,
        { code_name: newCodeName },
        { withCredentials: true },
      );
      toast.success('Code name updated');
      load();
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const runReminders = async () => {
    setRunningReminders(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/whatsapp/reminders/run`, {}, { withCredentials: true },
      );
      toast.success(`Fired ${data.fired}, skipped ${data.skipped} (window ${data.window_hours[0]}-${data.window_hours[1]}h)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to run reminders');
    } finally {
      setRunningReminders(false);
    }
  };

  const summary = useMemo(() => {
    const s = { sent: 0, failed: 0, skipped: 0, other: 0 };
    for (const l of logs) {
      if (l.status === 'sent' || l.status === 'delivered') s.sent += 1;
      else if (l.status === 'failed') s.failed += 1;
      else if (String(l.status || '').startsWith('skipped')) s.skipped += 1;
      else s.other += 1;
    }
    return s;
  }, [logs]);

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="admin-whatsapp-page">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 font-mono mb-1">Admin · Communications</div>
              <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white" data-testid="admin-whatsapp-title">
                WhatsApp Logs
              </h1>
              <p className="text-[#6B6B6B] dark:text-zinc-400 text-sm mt-1">All Interakt sends across the platform · templates registry · retry & reminders</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={runReminders}
                disabled={runningReminders || !config?.enabled}
                className="bg-[#FF451A] hover:bg-[#E63E17] text-[#0F0F0F] dark:text-white rounded-full px-4 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
                data-testid="run-reminders-btn"
                title="Manually run the 48-hour draft reminder scanner"
              >
                {runningReminders ? <Loader2 size={12} className="animate-spin" /> : <Bell size={12} />}
                Run 48h Reminders
              </Button>
              <Button
                onClick={load}
                className="bg-white/5 hover:bg-white/10 text-[#0F0F0F] dark:text-white border border-black/[0.10] dark:border-white/10 rounded-full px-4 py-2 text-xs inline-flex items-center gap-1.5"
                data-testid="refresh-btn"
              >
                <RefreshCw size={12} /> Refresh
              </Button>
            </div>
          </div>

          {/* Config banner */}
          <div
            className={`rounded-2xl p-4 border ${config?.enabled ? 'bg-[#00C972]/5 border-[#00C972]/20' : 'bg-yellow-500/5 border-yellow-500/20'}`}
            data-testid="config-banner"
          >
            <div className="flex items-center gap-3">
              <MessageSquare size={20} className={config?.enabled ? 'text-[#25D366]' : 'text-yellow-500'} />
              <div className="flex-1">
                <div className="text-sm font-semibold text-[#0F0F0F] dark:text-white">
                  Interakt integration: {config?.enabled
                    ? <span className="text-[#25D366]">ACTIVE</span>
                    : <span className="text-yellow-500">INACTIVE (env vars not set)</span>}
                </div>
                <div className="text-[11px] text-[#6B6B6B] dark:text-zinc-400 font-mono">
                  {config?.enabled
                    ? `Sender: ${config.sender || '(not set)'} · Rate: ${config.rate_per_second} msg/s`
                    : 'Set INTERAKT_API_KEY and WHATSAPP_SENDER_NUMBER in backend .env to activate.'}
                </div>
              </div>
            </div>
          </div>

          {/* Summary tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-whatsapp-summary">
            <Tile label="Total" value={logs.length} color="#71717a" testid="tile-total" />
            <Tile label="Sent" value={summary.sent} color="#00C972" testid="tile-sent" />
            <Tile label="Failed" value={summary.failed} color="#FF3B30" testid="tile-failed" />
            <Tile label="Skipped" value={summary.skipped} color="#FFB800" testid="tile-skipped" />
          </div>

          {/* Filters */}
          <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter size={12} className="text-[#6B6B6B] dark:text-zinc-500" />
              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 font-mono">Filters</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
              <FilterInput label="Campaign ID"    value={filterCampaign} onChange={setFilterCampaign} testid="filter-campaign" />
              <FilterSelect label="Template" value={filterTemplate} onChange={setFilterTemplate} testid="filter-template"
                options={[{ v: '', l: 'All' }, ...templates.map((t) => ({ v: t.code_name, l: t.code_name }))]} />
              <FilterSelect label="Status" value={filterStatus} onChange={setFilterStatus} testid="filter-status"
                options={[
                  { v: '', l: 'All' },
                  { v: 'sent', l: 'Sent' },
                  { v: 'delivered', l: 'Delivered' },
                  { v: 'failed', l: 'Failed' },
                  { v: 'skipped_no_config', l: 'Skipped (no config)' },
                  { v: 'skipped_bad_phone', l: 'Skipped (bad phone)' },
                ]} />
              <FilterInput label="From date" value={filterFrom} onChange={setFilterFrom} type="date" testid="filter-from" />
              <FilterInput label="To date" value={filterTo} onChange={setFilterTo} type="date" testid="filter-to" />
            </div>
            <div className="mt-3">
              <Button
                onClick={load}
                className="bg-[#25D366] hover:bg-[#20B85A] text-[#0F0F0F] dark:text-white text-xs rounded-full px-4 py-2"
                data-testid="apply-filters-btn"
              >
                Apply
              </Button>
            </div>
          </div>

          {/* Logs table */}
          <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]" data-testid="logs-table">
              <thead className="bg-white/[0.03] border-b border-black/[0.08] dark:border-white/5">
                <tr className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 font-mono">
                  <th className="text-left py-2.5 px-3">Time</th>
                  <th className="text-left py-2.5 px-2">Recipient</th>
                  <th className="text-left py-2.5 px-2">Template</th>
                  <th className="text-left py-2.5 px-2">Event</th>
                  <th className="text-left py-2.5 px-2">Status</th>
                  <th className="text-right py-2.5 px-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {loading ? (
                  <tr><td colSpan={6} className="py-8 text-center text-[#6B6B6B] dark:text-zinc-500"><Loader2 className="animate-spin inline mr-2" size={14} />Loading…</td></tr>
                ) : logs.length === 0 ? (
                  <tr><td colSpan={6} className="py-8 text-center text-[#6B6B6B] dark:text-zinc-500 text-sm" data-testid="logs-empty">No logs yet.</td></tr>
                ) : logs.map((l) => (
                  <React.Fragment key={l.id}>
                    <tr
                      className="hover:bg-white/[0.02] cursor-pointer"
                      onClick={() => setExpandedRow(expandedRow === l.id ? null : l.id)}
                      data-testid={`log-row-${l.id}`}
                    >
                      <td className="py-2 px-3 text-[11px] font-mono text-zinc-300">{fmtTime(l.created_at)}</td>
                      <td className="py-2 px-2 text-xs">{l.recipient_phone || '—'}</td>
                      <td className="py-2 px-2 text-xs font-mono text-zinc-300">{l.template_name}</td>
                      <td className="py-2 px-2 text-[11px] font-mono text-[#6B6B6B] dark:text-zinc-500">{l.event_type || '—'}</td>
                      <td className="py-2 px-2">
                        <span
                          className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border"
                          style={{ color: statusColor(l.status), borderColor: `${statusColor(l.status)}40`, backgroundColor: `${statusColor(l.status)}10` }}
                        >
                          {l.status}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right">
                        {l.status === 'failed' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); retry(l.id); }}
                            className="text-[11px] px-2 py-1 rounded-full bg-white/5 hover:bg-[#FF451A] hover:text-white text-zinc-300 border border-black/[0.10] dark:border-white/10 inline-flex items-center gap-1"
                            data-testid={`retry-${l.id}`}
                          >
                            <RefreshCw size={10} /> Retry
                          </button>
                        )}
                      </td>
                    </tr>
                    {expandedRow === l.id && (
                      <tr>
                        <td colSpan={6} className="bg-white/[0.02] px-3 py-3">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px] font-mono">
                            <KV label="Campaign ID" value={l.campaign_id || '—'} />
                            <KV label="Interakt Message ID" value={l.interakt_message_id || '—'} />
                            <KV label="Retry count" value={l.retry_count || 0} />
                            <KV label="Language" value={l.language_code} />
                            <KV label="Callback data" value={l.callback_data || '—'} />
                            <KV label="Error" value={l.error || '—'} color={l.error ? '#FF3B30' : undefined} />
                          </div>
                          <div className="mt-3">
                            <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 font-mono mb-1">Body values</div>
                            <pre className="text-[10px] bg-black/50 rounded-lg p-2 text-zinc-300 overflow-x-auto">
                              {JSON.stringify(l.body_values || [], null, 2)}
                            </pre>
                          </div>
                          {l.api_response && (
                            <div className="mt-3">
                              <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 font-mono mb-1">API response</div>
                              <pre className="text-[10px] bg-black/50 rounded-lg p-2 text-zinc-300 overflow-x-auto max-h-40">
                                {JSON.stringify(l.api_response, null, 2)}
                              </pre>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Templates registry */}
          <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-lg font-bold text-[#0F0F0F] dark:text-white">Templates Registry</h2>
                <p className="text-[11px] text-[#6B6B6B] dark:text-zinc-500 font-mono mt-1">
                  Rename each `code_name` to match your approved Interakt template, then toggle Active.
                </p>
              </div>
            </div>
            <div className="space-y-2" data-testid="templates-list">
              {templates.map((t) => (
                <div
                  key={t.id}
                  className={`grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center bg-white/[0.02] rounded-xl p-3 border ${
                    t.is_active ? 'border-[#25D366]/30' : 'border-black/[0.08] dark:border-white/5'
                  }`}
                  data-testid={`template-${t.event_type}`}
                >
                  <div>
                    <div className="text-sm font-semibold text-[#0F0F0F] dark:text-white">{t.display_name}</div>
                    <div className="text-[10px] text-[#6B6B6B] dark:text-zinc-500 font-mono">Event: {t.event_type} · Audience: {t.audience}</div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-mono block mb-1 text-[#0F0F0F] dark:text-white">Interakt code name</label>
                    <Input
                      defaultValue={t.code_name}
                      onBlur={(e) => updateTemplateCodeName(t, e.target.value.trim())}
                      className="h-7 text-xs font-mono bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                      data-testid={`template-code-${t.event_type}`}
                    />
                  </div>
                  <div className="text-[11px] font-mono text-[#6B6B6B] dark:text-zinc-400 truncate">
                    {(t.placeholders || []).join(', ') || '—'}
                  </div>
                  <button
                    onClick={() => toggleTemplate(t)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-wider inline-flex items-center gap-1 border ${
                      t.is_active
                        ? 'bg-[#25D366]/10 text-[#25D366] border-[#25D366]/30'
                        : 'bg-white/5 text-[#6B6B6B] dark:text-zinc-400 border-black/[0.10] dark:border-white/10 hover:bg-white/10'
                    }`}
                    data-testid={`toggle-template-${t.event_type}`}
                  >
                    {t.is_active ? <ToggleRight size={12} /> : <ToggleLeft size={12} />}
                    {t.is_active ? 'Active' : 'Inactive'}
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-black/[0.08] dark:border-white/5 text-[10px] text-[#6B6B6B] dark:text-zinc-500 font-mono">
              <div className="flex items-center gap-1"><Copy size={10} /> Suggested cron for the 48h reminder:</div>
              <code className="text-[10px] block mt-1">
                0 */6 * * * curl -X POST -H {'"Cookie: <admin_session>"'} $API/api/admin/whatsapp/reminders/run
              </code>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Sub-components ----------------------------------------------------------
const Tile = ({ label, value, color, testid }) => (
  <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1 font-mono">{label}</div>
    <div className="text-2xl font-bold font-mono" style={{ color }}>{value}</div>
  </div>
);

const FilterInput = ({ label, value, onChange, type = 'text', testid }) => (
  <div>
    <label className="text-[10px] font-mono uppercase block mb-1 text-[#0F0F0F] dark:text-white">{label}</label>
    <Input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 text-xs bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
      data-testid={testid}
    />
  </div>
);

const FilterSelect = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="text-[10px] font-mono uppercase block mb-1 text-[#0F0F0F] dark:text-white">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-8 text-xs rounded-md px-2 bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
      data-testid={testid}
    >
      {options.map((o) => (
        <option key={o.v} value={o.v}>{o.l}</option>
      ))}
    </select>
  </div>
);

const KV = ({ label, value, color }) => (
  <div>
    <div className="text-[9px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-0.5">{label}</div>
    <div className="text-xs truncate" style={{ color: color || '#e5e7eb' }}>{String(value)}</div>
  </div>
);
